import { BOT_PERSONALITIES } from '@src/constants/Domain';
import { GOOGLE_ANALYTICS } from '@src/const';
import { sendGoogleAnalyticsEvents } from '@src/actions/_utils';
import { DEFAULT_LANGUAGE } from '../../../constants/MogamboConstants';

const STATUS_ACTIVE = { title: 'Active', value: true };
const STATUS_INACTIVE = { title: 'Inactive', value: true };
const DEFAULT_ADVANCED_PROPERTIES = {
  active: { title: 'Active', value: true },
  gender: 'Male',
  companyName: 'Haptik',
};
export const SUBTLE_NOD_FREQUENCY_OPTIONS = [
  {
    name: 'Off — The Bot will not give any Subtle Nods.',
    value: 0,
  },
  {
    name: 'Every message — The bot gives a Subtle Nod before every response.',
    value: 1,
  },
  {
    name:
      'Every 2 messages — The bot gives a Subtle Nod in every alternate response.',
    value: 2,
  },
  {
    name:
      'Every 3 messages — The bot gives a Subtle Nod once every three responses.',
    value: 3,
  },
  {
    name:
      'Every 4 messages — The bot gives a Subtle Nod once every four responses.',
    value: 4,
  },
  {
    name:
      'Every 5 messages — The bot gives a Subtle Nod once every five responses.',
    value: 5,
  },
];

export function generateThresholdData(data) {
  const thresholds = [
    {
      name: 'Sensim V1',
      status: data.sensim_v1_state,
      priority: data.sensim_priority.indexOf('sensim_v1') + 1,
      threshold: data.sensim_v1_threshold,
      flag: 'ON',
      classifier: JSON.stringify(data.classifier_JSON),
    },
    {
      name: 'Sensim V2',
      status: data.sensim_v2_state,
      priority: data.sensim_priority.indexOf('sensim_v2') + 1,
      threshold: data.sensim_v2_threshold,
      flag: 'OFF',
      classifier: '',
    },
    {
      name: 'Sensim V3',
      status: data.sensim_v3_state,
      priority: data.sensim_priority.indexOf('sensim_v3') + 1,
      threshold: data.sensim_v3_threshold,
      flag: 'OFF',
      classifier: '',
    },
    {
      name: 'Global Sensim Threshold',
      status: true,
      priority: 5,
      threshold: data.global_threshold,
      flag: 'NA',
      classifier: '',
    },
    {
      name: 'Global Sensim Diff Threshold',
      status: true,
      priority: 5,
      threshold: data.global_diff_threshold,
      flag: 'NA',
      classifier: '',
    },
  ];

  thresholds.sort((a, b) => {
    return a.priority - b.priority;
  });

  return thresholds;
}

export function getBotMetadata(languageList = []) {
  const personalities = Object.keys(BOT_PERSONALITIES).map((personaKey, i) => ({
    title: BOT_PERSONALITIES[personaKey],
    key: i,
  }));

  return {
    personalities,
    languages: languageList,
  };
}

export function getBotData({
  botId,
  basicProperties,
  advancedProperties,
  integration_config,
  onError,
  editMode,
  fallbackAdvancedProperties = DEFAULT_ADVANCED_PROPERTIES,
}) {
  const basicPropertiesValid = basicProperties.validate();
  const advancedPropertiesValid = advancedProperties
    ? advancedProperties.validate()
    : true;
  if (!basicPropertiesValid || !advancedPropertiesValid) {
    onError(
      'Invalid Bot Properties Detected! Please review the highlighted fields in red.'
    );
    return null;
  }

  const {
    chatflowName,
    description,
    testBusiness,
    testPartner,
    language,
    enableHinglish,
  } = basicProperties.state;

  const {
    active,
    gender,
    companyName,
    appName,
    feedbackSmartActionLink,
    companyWebsite,
    botLocation,
    botPersona,
    companyContext,
    contextEnhancementInstr,
    intentDetectionInstr,
    answeringInstr,
    aiAgentAuthEnabled,
    aiAgentSessionTimeout,
    aiAgentSessionTimeoutEnabled,
    addDateAndTimeInPrompt,
    detectLanguageForAllMessages,
    aiAgentVoiceEnabled,
    aIAgentVoiceModel,
    aiAgentTtsEnabled,
    aiAgentPhoneNumber,
    aiAgentVoiceForOutput,
    aiAgentGreetingMessage,
    aiAgentVoiceProvider,
    botContext,
    aiAgentVoiceLocale,
    aIAgentOptionDisplayStyle,
    clearContextOnChatCompletion,
    aiAgentOzonetelSipNumber,
    aiAgentBackgroundNoiseEnabled,
    aiAgentVoiceTelephonyPlatform,
    ttsProviderSpeed,
    ttsProviderStability,
    ttsProviderStyle,
    ttsProviderSimilarityBoost,
    ttsProviderUseSpeakerBoost,
    aiAgentTextPipelineEnabled,
    aiAgentVoiceSubtleNodFrequency,
    isVoiceOutgoingWebhooksEnabled,
    webhookUrl,
    isVoiceRecordingEnabled,
    preRecordedAudioUrl,
    aiAgentHumanAgentTransferEnabled,
    aiAgentToolCallExecutionEnabled,
    aiAgentBackgroundNoiseVolume,
    voiceFreePbxAriUrl,
    voiceFreePbxAriUsername,
    voiceFreePbxAriPassword,
    voiceFreePbxExtension,
    botSettings,
  } = advancedProperties
    ? advancedProperties.state
    : fallbackAdvancedProperties;

  const ttsProviderConfig = [
    {
      similarity_boost: ttsProviderSimilarityBoost ?? 0.75,
      speed: ttsProviderSpeed ?? 1,
      stability: ttsProviderStability ?? 0.5,
      style: ttsProviderStyle ?? 0,
      use_speaker_boost: ttsProviderUseSpeakerBoost ?? false,
    },
    { speed: ttsProviderSpeed ?? 1.1 },
    { speed: ttsProviderSpeed ?? 0.0 },
  ];

  /**
   * Since Domain name is not to be rendered on UI,
   * chatflowName will have same rules as domainName and
   * same will be passed to domain_name
   *
   * --------
   *
   * When Editing a bot, domain_name and via_name should not be changed.
   * Hence, added editMode param
   *
   * --------
   *
   * Formatting of via_name is same as it exists currently.
   * eg. If chatflowName is entered as 'test_123',
   * via_name would be formatted as 'test123channel'
   */
  const newBot = {
    domain_name: editMode
      ? basicProperties.state.domainName.toLowerCase()
      : chatflowName.toLowerCase(),
    partner_name: testPartner.partner_name,
    business_name: testBusiness.toLowerCase(),
    via_name: editMode
      ? basicProperties.state.business_via_name.toLowerCase()
      : `${testBusiness
          .toLowerCase()
          .replace(/_/g, '')
          .replace(/ /g, '')}channel`,
    active: active.value,
    description,
    integration_config,
    response_format: aIAgentOptionDisplayStyle.id,
    ai_agent_auth_enabled: aiAgentAuthEnabled,
    detect_language_for_all_messages: detectLanguageForAllMessages,
    clear_context_on_chat_completion: clearContextOnChatCompletion,
    ai_agent_session_timeout: aiAgentSessionTimeout,
    ai_agent_session_timeout_enabled: aiAgentSessionTimeoutEnabled,
    add_date_and_time_in_prompt: addDateAndTimeInPrompt,
    voice_enabled: aiAgentVoiceEnabled,
    telephony_platform: aiAgentVoiceTelephonyPlatform.name,
    voice_model_id: aIAgentVoiceModel?.id,
    tts_enabled: aiAgentTtsEnabled,
    phone_number: aiAgentPhoneNumber,
    voice_config: {
      voice_id: aiAgentVoiceForOutput?.id,
      voice_name: aiAgentVoiceForOutput?.name,
      tts_provider_id: aiAgentVoiceForOutput?.ttsProviderId,
      greeting_msg: aiAgentGreetingMessage,
      provider: aiAgentVoiceProvider,
      locale: aiAgentVoiceLocale?.id,
      ozonetel_sip_reg_number: aiAgentOzonetelSipNumber,
      tts_provider_config: aiAgentTtsEnabled
        ? ttsProviderConfig[aiAgentVoiceForOutput?.ttsProviderId]
        : {},
      use_text_llm_model: aiAgentTextPipelineEnabled,
      subtle_nod_frequency: aiAgentVoiceSubtleNodFrequency?.value,
      is_voice_outgoing_webhooks_enabled: isVoiceOutgoingWebhooksEnabled,
      webhook_url: webhookUrl,
      is_voice_recording_enabled: isVoiceRecordingEnabled,
      pre_recorded_audio_url: preRecordedAudioUrl,
      human_agent_transfer_enabled: aiAgentHumanAgentTransferEnabled,
      ack_tool_execution: aiAgentToolCallExecutionEnabled,
      background_noise_volume: aiAgentBackgroundNoiseVolume,
      telephony_config:
        aiAgentVoiceTelephonyPlatform.name === 'freepbx'
          ? {
              freepbx_ari_url: voiceFreePbxAriUrl,
              freepbx_ari_username: voiceFreePbxAriUsername,
              freepbx_ari_password: voiceFreePbxAriPassword,
              freepbx_extension: voiceFreePbxExtension,
            }
          : {},
    },
    bot_properties: {
      bot_context: botContext,
      company_context: companyContext,
      context_enhancement_instr: contextEnhancementInstr,
      intent_detection_instr: intentDetectionInstr,
      answering_instr: answeringInstr,
      persona: botPersona,
      reply_tags: {
        [language?.code || DEFAULT_LANGUAGE]: {
          '%appname': appName,
          '%botgender': gender,
          '%botname': chatflowName,
          '%company': companyName,
          '%feedbacklink': feedbackSmartActionLink,
          '%location': botLocation,
          '%sitelink': companyWebsite,
        },
      },
    },
    hybrid_languages: {
      hinglish: enableHinglish,
    },
    bot_settings: botSettings || [],
  };

  if (botId) {
    newBot.domain_id = botId;
  }
  return newBot;
}

export function getBotProperties(
  botData,
  botProperties,
  botMetadata,
  domainMetaData
) {
  const languageCode = Object.keys(botProperties.reply_tags)[0];
  const replyTags = botProperties.reply_tags[languageCode];
  const basicProperties = {
    chatflowName: replyTags['%botname'] || botData.title,
    domainName: botData.title,
    description: botData.description,
    testBusiness:
      botData.title ||
      _getBusinessFromViaName(botMetadata, botData.test_business),
    testPartner: _getPartnerFromName(botMetadata, botData.client),
    language: _getLanguageFromCode(botMetadata, languageCode),
  };

  if (domainMetaData?.dynamic_settings) {
    domainMetaData.dynamic_settings.forEach((setting) => {
      const { key, default: defaultValue } = setting;
      const isValuePresentInBotSettings = botData.bot_settings
        ? botData.bot_settings[key] !== undefined
        : false;
      if (defaultValue !== undefined && !isValuePresentInBotSettings) {
        botData.bot_settings = {
          ...botData.bot_settings,
          [key]: defaultValue,
        };
      }
    });
  }

  const advancedProperties = {
    botContext: botProperties.bot_context,
    companyContext: botProperties.company_context,
    active: botData.active ? STATUS_ACTIVE : STATUS_INACTIVE,
    gender: replyTags['%botgender'] || 'Male',
    companyName: replyTags['%company'],
    appName: replyTags['%appname'],
    feedbackSmartActionLink: replyTags['%feedbacklink'],
    companyWebsite: replyTags['%sitelink'],
    botLocation: replyTags['%location'],
    botPersona: botProperties.persona || 0,
    contextEnhancementInstr: botProperties.context_enhancement_instr,
    intentDetectionInstr: botProperties.intent_detection_instr,
    answeringInstr: botProperties.answering_instr,
    aiAgentAuthEnabled: botData.ai_agent_auth_enabled,
    detectLanguageForAllMessages: botData.detect_language_for_all_messages,
    clearContextOnChatCompletion: botData.clear_context_on_chat_completion,
    aiAgentSessionTimeout: botData.ai_agent_session_timeout,
    aiAgentSessionTimeoutEnabled: botData.ai_agent_session_timeout_enabled,
    addDateAndTimeInPrompt: botData.add_date_and_time_in_prompt,
    aiAgentVoiceEnabled: botData.voice_enabled,
    aIAgentVoiceModel: {
      id: botData.voice_model_id,
      name: botData.voice_model_name,
    },
    aiAgentVoiceTelephonyPlatform: { name: botData.telephony_platform },
    aiAgentTtsEnabled: botData.tts_enabled,
    aiAgentPhoneNumber: botData.phone_number,
    aiAgentVoiceForOutput: {
      id: botData.voice_config?.voice_id,
      name: botData.voice_config?.voice_name || botData.voice_config?.voice_id,
      ttsProviderId: botData.voice_config?.tts_provider_id,
    },
    aiAgentGreetingMessage: botData.voice_config?.greeting_msg,
    aiAgentVoiceProvider: botData.voice_config?.provider,
    aiAgentTextPipelineEnabled: botData.voice_config?.use_text_llm_model,
    aiAgentVoiceLocale: botData.voice_config?.locale,
    aIAgentOptionDisplayStyle: { id: botData.response_format },
    aiAgentOzonetelSipNumber: botData.voice_config?.ozonetel_sip_reg_number,
    aiAgentVoiceSubtleNodFrequency: SUBTLE_NOD_FREQUENCY_OPTIONS.find(
      (frequency) =>
        frequency.value === botData.voice_config.subtle_nod_frequency
    ),
    ttsProviderSpeed:
      botData.voice_config?.tts_provider_config?.speed ??
      (botData.voice_config?.tts_provider_id === 1 ? 1.1 : 1),
    ttsProviderStability:
      botData.voice_config?.tts_provider_config?.stability ?? 0.5,
    ttsProviderStyle: botData.voice_config?.tts_provider_config?.style ?? 0,
    ttsProviderSimilarityBoost:
      botData.voice_config?.tts_provider_config?.similarity_boost ?? 0.75,
    ttsProviderUseSpeakerBoost:
      botData.voice_config?.tts_provider_config?.use_speaker_boost ?? false,
    isVoiceOutgoingWebhooksEnabled:
      botData.voice_config?.is_voice_outgoing_webhooks_enabled,
    webhookUrl: botData.voice_config?.webhook_url,
    isVoiceRecordingEnabled: botData.voice_config?.is_voice_recording_enabled,
    preRecordedAudioUrl: botData.voice_config?.pre_recorded_audio_url,
    aiAgentHumanAgentTransferEnabled:
      botData.voice_config?.human_agent_transfer_enabled,
    aiAgentToolCallExecutionEnabled: botData.voice_config?.ack_tool_execution,
    aiAgentBackgroundNoiseVolume:
      botData.voice_config?.background_noise_volume || 0,
    voiceFreePbxAriUrl: botData.voice_config?.telephony_config
      ? botData.voice_config.telephony_config.freepbx_ari_url
      : '',
    voiceFreePbxAriUsername: botData.voice_config?.telephony_config
      ? botData.voice_config.telephony_config.freepbx_ari_username
      : '',
    voiceFreePbxAriPassword: botData.voice_config?.telephony_config
      ? botData.voice_config.telephony_config.freepbx_ari_password
      : '',
    voiceFreePbxExtension: botData.voice_config?.telephony_config
      ? botData.voice_config.telephony_config.freepbx_extension
      : '',
    botSettings: botData.bot_settings || {},
    dynamicSettings: domainMetaData.dynamic_settings || [],
  };

  return { basicProperties, advancedProperties };
}

export function sendAnalytics(
  testBusiness,
  testPartner,
  updatedBotEnv,
  domainName
) {
  const newPartnerAdded = testPartner.type === 'CREATE';
  const newBusinessAdded = testBusiness.type === 'CREATE';

  let gaLabel = GOOGLE_ANALYTICS.LABEL.OLD_BUSINESS_OLD_PARTNER;
  if (newPartnerAdded && newBusinessAdded) {
    gaLabel = GOOGLE_ANALYTICS.LABEL.NEW_BUSINESS_NEW_PARTNER;
  } else if (newPartnerAdded && !newBusinessAdded) {
    gaLabel = GOOGLE_ANALYTICS.LABEL.OLD_BUSINESS_NEW_PARTNER;
  } else if (!newPartnerAdded && newBusinessAdded) {
    gaLabel = GOOGLE_ANALYTICS.LABEL.NEW_BUSINESS_OLD_PARTNER;
  }

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.BOT,
    GOOGLE_ANALYTICS.EVENTS.BOT_CREATE,
    gaLabel,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  if (newBusinessAdded) {
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.BUSINESS,
      GOOGLE_ANALYTICS.EVENTS.BUSINESS_CREATE_VIA_BOTFLOW,
      testBusiness.viaName,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  }

  if (updatedBotEnv.base_url_test !== undefined) {
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.BOT,
      `${GOOGLE_ANALYTICS.EVENTS.BOT_EDIT}-${domainName}`,
      `base-url-test`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  }

  if (updatedBotEnv.base_url_production !== undefined) {
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.BOT,
      `${GOOGLE_ANALYTICS.EVENTS.BOT_EDIT}-${domainName}`,
      `base-url-prod`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  }
  if (updatedBotEnv.active_env !== undefined) {
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.BOT,
      `${GOOGLE_ANALYTICS.EVENTS.BOT_EDIT}-${domainName}`,
      `active-env`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  }
}

function _getBusinessFromViaName(botMetadata, viaName) {
  return botMetadata.businesses.filter(
    (business) => business.viaName === viaName
  )[0];
}

function _getPartnerFromName(botMetadata, partnerName) {
  return botMetadata.partners.filter(
    (partner) => partner.partner_name === partnerName
  )[0];
}

function _getLanguageFromCode(botMetadata, languageCode) {
  return botMetadata.languages.filter(
    (language) => language.code === languageCode
  )[0];
}

export const keyToStateMap = {
  telephony_platform: 'aiAgentVoiceTelephonyPlatform.name',
  voice_model_id: 'aIAgentVoiceModel.id',
  voice_model_name: 'aIAgentVoiceModel.name',
  tts_provider_id: 'aiAgentVoiceForOutput.ttsProviderId',
  voice_id: 'aiAgentVoiceForOutput.id',
  voice_name: 'aiAgentVoiceForOutput.name',
  use_text_llm_model: 'aiAgentTextPipelineEnabled',
  voice_enabled: 'aiAgentVoiceEnabled',
  tts_enabled: 'aiAgentTtsEnabled',
  locale: 'aiAgentVoiceLocale.id',
};
